import { usePauseDialogs } from '@/shared/stores/pause-dialogs.store';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ReadinessAction } from '@unipostudio/shared-validators';
import { useChannelOAuth } from '@/shared/hooks/useChannelOAuth';
import type { PlatformId } from '@/shared/components/PlatformIcon';
import { ROUTES } from '@/shared/constants/routes';

export interface ReadinessTarget {
  postId: string;
  /** As the API names it (INSTAGRAM), when the issue is one channel's. */
  platform?: string | null;
}

/** Channels connected by a form (token, bot) rather than an OAuth popup: reconnected on the Channels page. */
const MANUAL_CONNECT: ReadonlySet<string> = new Set(['TELEGRAM', 'WHATSAPP']);

/** The in-app route an action opens, for a link or a fallback. */
export function readinessActionRoute(action: ReadinessAction, target: ReadinessTarget): string {
  switch (action) {
    case 'reconnect':
      return ROUTES.CHANNELS;
    case 'edit_post':
    case 'replace_media':
      // A post still waiting to publish is edited in place.
      return `${ROUTES.COMPOSER}/${target.postId}`;
    case 'wait':
      return ROUTES.MEDIA;
    case 'buy_x_credits':
    case 'choose_plan':
      return `${ROUTES.SETTINGS}?tab=subscription`;
    case 'approve':
      return ROUTES.APPROVALS;
    case 'resume_publishing':
      return `${ROUTES.SETTINGS}?tab=brand`;
  }
}

/**
 * Runs the one action a pre-publish issue offers (Task 1.2): reconnect opens
 * the channel's sign-in at once (a form-connected channel opens Channels);
 * everything else opens the screen that fixes it.
 */
export function useReadinessAction(): { run: (action: ReadinessAction, target: ReadinessTarget) => Promise<void>; busy: boolean } {
  const navigate = useNavigate();
  const oauth = useChannelOAuth();
  const [busy, setBusy] = useState(false);

  const run = async (action: ReadinessAction, target: ReadinessTarget): Promise<void> => {
    if (action === 'reconnect' && target.platform && !MANUAL_CONNECT.has(target.platform.toUpperCase())) {
      setBusy(true);
      try {
        await oauth.connect(target.platform.toLowerCase() as PlatformId);
      } finally {
        setBusy(false);
      }
      return;
    }
    if (action === 'resume_publishing') {
      usePauseDialogs.getState().show('resume');
      return;
    }
    void navigate(readinessActionRoute(action, target));
  };

  return { run, busy };
}
